import type { QuestionId } from '../model/brands.ts';
import type { CanonicalExam, CanonicalQuestion } from '../model/canonical.ts';
import { isGraded, type Outcome, type QuestionScore } from '../model/session.ts';

/**
 * Turning a set of results into the numbers a person reads afterwards.
 *
 * Two decisions shape everything here.
 *
 * **The percentage is `Σraw / Σmax`, not `correct / total`.** Counting questions would make partial
 * credit and per-question weights invisible, and would have to be rewritten the moment either
 * arrives.
 *
 * **Ungraded questions leave the denominator.** A self-evaluated essay the app cannot mark must not
 * drag the score down; it is counted in the total, reported separately, and excluded from the
 * percentage until the candidate resolves it.
 */

export interface ScoredEntry {
  readonly questionId: QuestionId;
  readonly score: QuestionScore;
  readonly durationMs: number;
  readonly markedForReview: boolean;
}

export interface Breakdown {
  readonly id: string;
  readonly label: string;
  readonly total: number;
  readonly correct: number;
  readonly incorrect: number;
  readonly partial: number;
  readonly unanswered: number;
  readonly ungraded: number;
  readonly raw: number;
  readonly max: number;
  readonly percent: number;
}

export interface SessionSummary {
  readonly total: number;
  readonly answered: number;
  readonly unanswered: number;
  readonly correct: number;
  readonly incorrect: number;
  readonly partial: number;
  readonly ungraded: number;
  readonly rawScore: number;
  readonly maxScore: number;
  readonly scorePercent: number;
  readonly passed?: boolean;
  readonly durationMs: number;
  readonly averageMsPerQuestion: number;
  readonly markedForReviewCount: number;
  readonly byCategory: readonly Breakdown[];
  readonly byTopic: readonly Breakdown[];
  readonly byDifficulty: readonly Breakdown[];
  readonly byTag: readonly Breakdown[];
  readonly byType: readonly Breakdown[];
}

const round = (value: number, decimals = 1): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

export function percentOf(raw: number, max: number): number {
  return max <= 0 ? 0 : round((raw / max) * 100);
}

export function buildSummary(
  exam: CanonicalExam,
  entries: readonly ScoredEntry[],
  durationMs: number,
): SessionSummary {
  const counts: Record<Outcome, number> = {
    correct: 0,
    incorrect: 0,
    partial: 0,
    unanswered: 0,
    ungraded: 0,
  };

  let rawScore = 0;
  let maxScore = 0;
  let markedForReviewCount = 0;

  for (const entry of entries) {
    counts[entry.score.outcome] += 1;
    if (entry.markedForReview) {
      markedForReviewCount += 1;
    }
    if (isGraded(entry.score)) {
      rawScore += entry.score.raw;
      maxScore += entry.score.max;
    }
  }

  const total = entries.length;
  const answered = total - counts.unanswered;
  const passing = exam.meta.defaults.passingScorePercent;
  const scorePercent = percentOf(rawScore, maxScore);

  return {
    total,
    answered,
    unanswered: counts.unanswered,
    correct: counts.correct,
    incorrect: counts.incorrect,
    partial: counts.partial,
    ungraded: counts.ungraded,
    rawScore: round(rawScore, 2),
    maxScore: round(maxScore, 2),
    scorePercent,
    ...(passing !== undefined && { passed: scorePercent >= passing }),
    durationMs,
    // Over every question drawn, not only the answered ones: time spent on a question left blank
    // was still time spent.
    averageMsPerQuestion: total === 0 ? 0 : Math.round(durationMs / total),
    markedForReviewCount,
    byCategory: breakdownBy(exam, entries, categoryKeys, labelForTaxonomy(exam, 'categories')),
    byTopic: breakdownBy(exam, entries, topicKeys, labelForTaxonomy(exam, 'topics')),
    byDifficulty: breakdownBy(exam, entries, difficultyKeys),
    byTag: breakdownBy(exam, entries, tagKeys),
    byType: breakdownBy(exam, entries, typeKeys),
  };
}

type KeyExtractor = (question: CanonicalQuestion) => readonly string[];

const categoryKeys: KeyExtractor = (question) =>
  question.classification.categoryId === undefined ? [] : [question.classification.categoryId];
const topicKeys: KeyExtractor = (question) => question.classification.topicIds;
const difficultyKeys: KeyExtractor = (question) =>
  question.classification.difficulty === undefined ? [] : [question.classification.difficulty];
const tagKeys: KeyExtractor = (question) => question.classification.tags;
const typeKeys: KeyExtractor = (question) => [question.type];

function labelForTaxonomy(
  exam: CanonicalExam,
  field: 'categories' | 'topics',
): (id: string) => string {
  const labels = new Map(exam.taxonomy[field].map((node) => [node.id as string, node.label]));
  // Falling back to the id keeps an undeclared taxonomy usable rather than showing a blank row.
  return (id: string) => labels.get(id) ?? id;
}

function breakdownBy(
  exam: CanonicalExam,
  entries: readonly ScoredEntry[],
  keysOf: KeyExtractor,
  labelOf: (id: string) => string = (id) => id,
): readonly Breakdown[] {
  const buckets = new Map<string, { entries: ScoredEntry[] }>();

  for (const entry of entries) {
    const position = exam.index.byId[entry.questionId];
    const question = position === undefined ? undefined : exam.questions[position];
    if (question === undefined) {
      continue;
    }
    for (const key of keysOf(question)) {
      const bucket = buckets.get(key) ?? { entries: [] };
      bucket.entries.push(entry);
      buckets.set(key, bucket);
    }
  }

  return [...buckets.entries()]
    .map(([id, bucket]) => summarise(id, labelOf(id), bucket.entries))
    // Weakest first: the point of a per-area breakdown is to show where to study next.
    .sort((a, b) => a.percent - b.percent || a.id.localeCompare(b.id));
}

function summarise(id: string, label: string, entries: readonly ScoredEntry[]): Breakdown {
  const counts: Record<Outcome, number> = {
    correct: 0,
    incorrect: 0,
    partial: 0,
    unanswered: 0,
    ungraded: 0,
  };
  let raw = 0;
  let max = 0;

  for (const entry of entries) {
    counts[entry.score.outcome] += 1;
    if (isGraded(entry.score)) {
      raw += entry.score.raw;
      max += entry.score.max;
    }
  }

  return {
    id,
    label,
    total: entries.length,
    correct: counts.correct,
    incorrect: counts.incorrect,
    partial: counts.partial,
    unanswered: counts.unanswered,
    ungraded: counts.ungraded,
    raw: round(raw, 2),
    max: round(max, 2),
    percent: percentOf(raw, max),
  };
}
